import {
  AnalyticsActionBuyEvent,
  AnalyticsEventParam,
  PaymentMethod,
  PaymentMethodTypes,
  ReceiptStatus,
} from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import { FC, useMemo, useState } from 'react';

import { useConfig } from '#/hooks';
import { PaymentStore } from '#/plugins/paywall/models';

import { PaymentProps } from '../../types';
import PaymentCardForm from './components/PaymentCardForm';
import PaymentPayPalForm from './components/PaymentPayPalForm';
import { PaymentContext } from './context';
import { Discount } from './Discount';
import { SelectedPlan } from './SelectedPlan';
import * as Styled from './styled';

const payments = {
  creditCard: {
    name: 'Debit/Credit',
    icon: 'images/payment-card.svg',
  },
  paypal: {
    name: 'PayPal',
    icon: 'images/payment-paypal.png',
  },
};

const Payment: FC<PaymentProps> = (props) => {
  const {
    actions: { getProviderInfo, validateVoucher },
    previousStep,
    selectedPlan,
  } = props;

  const callPaymentAnalytics = async () => {
    // TODO#analytics: Remove MPP fallback here once we've updated the MPP plugin to provide this info
    const { name: storeName = PaymentStore.Mpp } = await getProviderInfo();
    // #ExternalAnalytics - Buy
    document.dispatchEvent(
      new AnalyticsActionBuyEvent({
        //[AnalyticsEventParam.TransactionId]: ?, TODO#analytics
        [AnalyticsEventParam.Price]: Number(selectedPlan?.price),
        [AnalyticsEventParam.Store]: storeName,
        [AnalyticsEventParam.Currency]: selectedPlan?.currency,
        [AnalyticsEventParam.ReceiptStatus]: ReceiptStatus.Successful,
        [AnalyticsEventParam.SubscriptionPlan]: selectedPlan?.title,
      })
    );
  };

  // TODO: selectedPlan shouldn't be optional here
  const [price, setPrice] = useState(selectedPlan?.price || '0');
  const [voucher, setVoucher] = useState<string>();
  const [isCardRequired, setIsCardRequired] = useState<boolean>(true);
  const availablePayments = selectedPlan?.paymentMethods;

  if (!selectedPlan) {
    console.warn('No selected plan was found.');

    return null;
  }

  return (
    <PaymentContext.Provider
      value={{ ...props, voucher, isCardRequired, callPaymentAnalytics }}
    >
      <Styled.Receipt>
        <SelectedPlan
          {...selectedPlan}
          onPlanChange={previousStep}
          price={price}
        />

        <Styled.ReceiptLineBreak />

        <Discount
          {...selectedPlan}
          onPriceChange={setPrice}
          onVoucherChange={(planId, voucher) =>
            validateVoucher(planId, voucher)
              .then((res) => {
                setVoucher(voucher);
                setIsCardRequired(res.isPaymentDetailsRequired);

                return res;
              })
              .catch((err) => {
                setVoucher(undefined);
                throw err;
              })
          }
          price={price}
        />
      </Styled.Receipt>

      {availablePayments?.length ? (
        <PaymentTabs availablePayments={availablePayments} />
      ) : (
        <PaymentCardForm />
      )}

      <Styled.PaymentFormFooter linkTarget="_blank">
        {t('web.iap.paymentFooter')}
      </Styled.PaymentFormFooter>
    </PaymentContext.Provider>
  );
};

const PaymentTabs = ({
  availablePayments,
}: {
  availablePayments: PaymentMethod[];
}) => {
  const [activeTabId, setActiveTabId] = useState(0);

  const {
    app: { staticFilePrefix },
  } = useConfig();

  const handleTabClick = (id) => () => {
    setActiveTabId(id);
  };

  const filteredPayments = useMemo(
    () => availablePayments.filter(({ type }) => type !== 'unknown'),
    [availablePayments]
  );

  if (filteredPayments.length === 1) {
    switch (availablePayments[0].type) {
      case PaymentMethodTypes.Paypal:
        return <PaymentPayPalForm />;
      case PaymentMethodTypes.CreditCard:
        return <PaymentCardForm />;
      default:
        return <></>;
    }
  }

  return (
    <>
      <Styled.Tabs role="tablist">
        {filteredPayments.map((payment, id) => (
          <Styled.Tab
            $selected={activeTabId === id}
            aria-controls={`payment-tabpanel${id}`}
            id={`payment-tab${id}`}
            key={id}
            onClick={handleTabClick(id)}
          >
            <Styled.Icon
              src={`${staticFilePrefix}${payments[payment.type].icon}`}
            ></Styled.Icon>
            {payments[payment.type].name}
          </Styled.Tab>
        ))}
      </Styled.Tabs>

      {filteredPayments.map((payment, id) => (
        <Styled.TabPanel
          $selected={activeTabId === id}
          aria-labelledby={`payment-tab${id}`}
          id={`payment-tabpanel${id}`}
          key={id}
        >
          {payment.type === 'paypal' ? (
            <PaymentPayPalForm />
          ) : (
            <PaymentCardForm />
          )}
        </Styled.TabPanel>
      ))}
    </>
  );
};

export default Payment;
